// Persistent floor-plan library (IndexedDB, own db) + JSON file export/import.

import { getModel, putModel } from "../model/collection-store";
import { type SavedModel, isSavedModel } from "../model/saved-model";
import { downloadJson, newId, safeFileName } from "../model/idb-store";
import { type SavedFloorPlan, isSavedFloorPlan } from "./saved-floor-plan";
import { createSyncedStore } from "../sync/synced-store";
import { syncController } from "../sync/controller";
import { createScopedStore } from "../sync/account-scope";

// Local IndexedDB, scoped per signed-in account (the legacy database is the
// anonymous pool) and mirrored to the server once a user signs in (a no-op
// passthrough until then). Reads stay local; writes queue for sync.
const store = createSyncedStore<SavedFloorPlan>(
  "layouts",
  createScopedStore<SavedFloorPlan>("tinydraft-floorplans", "plans"),
  syncController,
);

export function listPlans(): Promise<SavedFloorPlan[]> {
  return store.list();
}
export function putPlan(p: SavedFloorPlan): Promise<IDBValidKey> {
  return store.put(p);
}
export function deletePlan(id: string): Promise<undefined> {
  return store.remove(id);
}
export const newPlanId = newId;

// A floor-plan export bundles the models its placements reference, so a single
// file is fully portable (other devices/browsers don't have your collection).
// Models keep their ids; readPlanFile registers them on import so the
// placements resolve. The reference photo is stripped (browser-only + heavy).
interface ExportedPlan extends SavedFloorPlan {
  models?: SavedModel[];
}

export async function downloadPlan(p: SavedFloorPlan): Promise<void> {
  const ids = [...new Set(p.placements.map((pl) => pl.modelId))];
  const fetched = await Promise.all(ids.map((id) => getModel(id)));
  const models: SavedModel[] = [];
  for (const m of fetched) {
    if (!m) continue;
    const slim: SavedModel = { ...m };
    delete slim.reference;
    models.push(slim);
  }
  const exported: ExportedPlan = { ...p, models };
  downloadJson(exported, `${safeFileName(p.name, "floorplan")}.tinydraftplan.json`);
}

export async function readPlanFile(file: File): Promise<SavedFloorPlan> {
  const parsed: unknown = JSON.parse(await file.text());
  if (!isSavedFloorPlan(parsed)) throw new Error("Not a TinyDraft floor-plan file.");
  // Register any bundled models (keeping their ids) so the plan's placements can
  // be rebuilt from the collection — this is what makes a transferred plan show
  // its furniture. Older plan files have no `models` and rely on the collection.
  const bundled = (parsed as ExportedPlan).models;
  if (Array.isArray(bundled)) {
    for (const m of bundled) if (isSavedModel(m)) await putModel(m);
  }
  // Strip the bundle out of the stored plan (kept light); fresh id avoids
  // clobbering an existing plan.
  const { models: _ignored, ...plan } = parsed as ExportedPlan;
  return { ...plan, id: newPlanId() };
}
